import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { PageBlockRenderer } from "./PageBlockRenderer";
import { PageHtmlRenderer } from "./PageHtmlRenderer";

export function PagesApp() {
    const { page, setPage, setStripe, setCheckoutSettings } = usePage();
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const pathname = location.pathname;
        if (pathname.startsWith("/checkout/")) return;

        const fetchPage = async () => {
            setLoading(true);
            try {
                const response = await fetch(`/api${pathname}`);
                if (response.ok) {
                    const data = await response.json();
                    setPage(data.page);
                    if (data.stripe) {
                        setStripe(data.stripe);
                    }
                    if (data.checkoutSettings) {
                        setCheckoutSettings(data.checkoutSettings);
                    }
                } else {
                    console.error("Failed to fetch page:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching page:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPage();
    }, [location.pathname, setPage, setStripe, setCheckoutSettings]);

    useEffect(() => {
        if (page?.pageTitle) {
            document.title = page.pageTitle;
        }
    }, [page?.pageTitle]);

    useEffect(() => {
        let ogImageMeta = document.querySelector('meta[property="og:image"]');
        if (page?.metaImageUrl) {
            if (!ogImageMeta) {
                ogImageMeta = document.createElement('meta');
                ogImageMeta.setAttribute('property', 'og:image');
                document.head.appendChild(ogImageMeta);
            }
            ogImageMeta.setAttribute('content', page.metaImageUrl);
        } else {
            if (ogImageMeta) {
                ogImageMeta.remove();
            }
        }

        let twitterImageMeta = document.querySelector('meta[name="twitter:image"]');
        if (page?.metaImageUrl) {
            if (!twitterImageMeta) {
                twitterImageMeta = document.createElement('meta');
                twitterImageMeta.setAttribute('name', 'twitter:image');
                document.head.appendChild(twitterImageMeta);
            }
            twitterImageMeta.setAttribute('content', page.metaImageUrl);
        } else {
            if (twitterImageMeta) {
                twitterImageMeta.remove();
            }
        }
    }, [page?.metaImageUrl]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div>Loading...</div>
            </div>
        );
    }

    if (!page) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div>Page not found</div>
            </div>
        );
    }

    if (page.type === "html") {
        return (
            <PageHtmlRenderer
                html={page.html ?? ""}
                page={page}
                path={location.pathname}
            />
        );
    }

    return <PageBlockRenderer page={page} />;
}

export default PagesApp;
